<template lang="pug">
  .panel-page
    .panel.body-1.mx-auto
      FormErrorAlert(
        :message="message"
        v-show="message"
        @dismiss="message = ''"
      )
      h1 User Search
      p To search existing users, please enter the requested search criteria.

      form(
        @submit.prevent="submit"
        autocomplete="off"
      )
        FormHeader.mt-2(title="PERSONAL INFORMATION")
        .form-section.mt-2
          .form-row
            .col
              .form-group
                label(for="username") User Name
                input#username.form-control(v-model="username")
            .col
              .form-group
                label(for="last-name") Last Name
                input#last-name.form-control(v-model="lastName")
          .form-row
            .col
              .form-group
                label(for="email") Email Address
                input#email.form-control(
                  v-model="email"
                  :class="{'is-invalid': emailIfInvalid}"
                )
                FormError(
                  :message="emailError"
                  v-show="emailIfInvalid"
                )
        FormHeader.mt-2(
          title="LOCATION"
          v-if="$auth.internalAdmin()")
        .form-section.mt-2(v-if="$auth.internalAdmin()")
          .form-row
            .col
                .form-row
                  label(for="statesList") State(s)
                .form-row
                  .col
                    SuperSelect(
                      taggable
                      :options="filterStates"
                      label-name="name"
                      value-name="abbreviation"
                      v-model="selectedStates"
                      @filter="stateFilter = $event"
                      @input="refreshAgencies"
                    )
                
        FormHeader.mt-2(
          title="AGENCIES"
          v-if="$auth.stateAdmin()")
        .form-section.mt-2(v-if="$auth.stateAdmin()")
          .form-row
            .col
              CustomRadio(
                model-value="all"
                v-model="agencyType"
              )  All Agencies
          .form-row
            .col
              CustomRadio(
                model-value="states"
                v-model="agencyType"
              )  Selected Agencies
          .form-row(v-show="agencyType === 'states'")
            .col
              SuperSelect(
                :options="agencies"
                labelName="full_description"
                valueName="id"
                taggable
                v-model="selectedAgencies"
                @filter="filter"
                :valid="!selectedAgenciesInvalid"
              )
              FormError(v-show="selectedAgenciesInvalid") Agency is required.
              
        FormHeader.mt-2(title="USER ACCOUNT STATUS")
        .form-section.mt-2
          .form-row
            .col
              CustomCheckbox(
                :value="status.includes('active')"
                @input="toggle(status, 'active')"
              )  Active
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="status.includes('revoked')"
                @input="toggle(status, 'revoked')"
              )  Revoked
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="status.includes('unapproved')"
                @input="toggle(status, 'unapproved')"
              )  Unapproved
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="status.includes('locked')"
                @input="toggle(status, 'locked')"
              )  Locked
        
        FormHeader.mt-2(title="USER LEVEL")
        .form-section.mt-3
          .form-row
            .col
              CustomCheckbox(
                :value="userLevel.includes('basic')"
                @input="toggle(userLevel, 'basic')"
              )  Basic User
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="userLevel.includes('agency_admin')"
                @input="toggle(userLevel, 'agency_admin')"
              )  Agency User
          .form-row.mt-2(
            v-if="['state_admin', 'internal_admin'].includes(currentGroup())"
          )
            .col
              CustomCheckbox(
                :value="userLevel.includes('state_admin')"
                @input="toggle(userLevel, 'state_admin')"
              )  State User
          .form-row.mt-2(
            v-if="$auth.naddiAdmin()"
          )
            .col
              CustomCheckbox(
                :value="userLevel.includes('naddi_admin')"
                @input="toggle(userLevel, 'naddi_admin')"
              )  NADDI User
          .form-row.mt-2(
            v-if="$auth.internalAdmin()"
          )
            .col
              CustomCheckbox(
                :value="userLevel.includes('internal_admin')"
                @input="toggle(userLevel, 'internal_admin')"
              )  Internal (For Appriss Admin Only)
        
        button.btn.btn-primary(type="submit") Submit
</template>

<script>
import { currentGroup } from '@/lib/plugins/authorization'
import { mapTouched, mapRequiredIf } from '@/lib/validation'
import { toggle } from '@/lib/utils'
import axios from '@/lib/axios'
import CustomCheckbox from '@/components/CustomCheckbox'
import CustomRadio from '@/components/CustomRadio'
import debounce from '@/lib/debounce'
import FormHeader from '@/components/FormHeader'
import states from '../lib/states'
import stdmsg from '@/lib/stdmsg'
import store from '@/store'
import SuperSelect from '@/components/SuperSelect'
import { loadForm, formItem } from '@/lib/formstore'
import { getAgencies } from '@/lib/services/agencies'

export default {
  name: 'UserSearch',
  data() {
    return {
      message: '',
      emailError: stdmsg.emailError,
      agencyList: [],
      stateFilter: '',
      filter: debounce(value => this.filterQuery(value), 250),
      ...mapTouched(['email', 'agencyType']),
      username: '',
      lastName: '',
      email: '',
      selectedAgencies: [],
      agencyType: 'all',
      status: ['active'],
      selectedStates: [],
      userLevel: '',
    }
  },
  computed: {
    agencies() {
      if (this.agencyList) return this.agencyList
      else return []
    },
    ...mapRequiredIf(['email'], x => /[^@]+@.+\..+/.test(x)),
    selectedAgenciesInvalid() {
      return (
        this.agencyTypeTouched &&
        this.agencyType === 'states' &&
        this.selectedAgencies.length === 0
      )
    },
    filterStates() {
      return states.filter(st =>
        st.name.toLowerCase().includes(this.stateFilter.toLowerCase()),
      )
    },
  },
  methods: {
    currentGroup: currentGroup,
    submit() {
      this.emailTouched = true
      this.agencyTypeTouched = true
      if (this.emailIfInvalid || this.selectedAgenciesInvalid) {
        this.message = stdmsg.formError
        scrollTo(0, 0)
        return
      }
      this.$router.push({ name: 'user_search_results' })
    },
    agencyParams(name) {
      let p = { name }
      if (this.$auth.stateAdmin() && !this.$auth.internalAdmin()) {
        p.state = this.$store.state.account.states.join(',')
      }
      const extraStates = this.selectedStates.map(x => x.abbreviation).join(',')
      if (extraStates) {
        if (p.state == undefined) {
          p.state = ''
        }
        p.state = [p.state, extraStates].filter(x => x).join(',')
      }
      return p
    },
    refreshAgencies(value) {
      this.filterQuery(value)
    },
    toggle,
    getSearchAgencies(name) {
      getAgencies({ name: name || '' })
        .then(resp => (this.agencyList = resp.data))
        .catch(err => globalError(err))
    },
    filterQuery(value) {
      axios({ loaderDelay: 0 })
        .get('/api/misc/agencies', { params: this.agencyParams(value) })
        .then(resp => (this.agencyList = resp.data))
    },
    checkRefreshAgencies() {
      let item = formItem('usersearch', 'selectedStates') || ''
      if (item.length > 0) {
        this.refreshAgencies(formItem('usersearch', 'selectedStates'))
      } else {
        this.getSearchAgencies()
      }
    },
  },
  mounted() {
    let defaultSelected = ['basic']
    if (this.$auth.agencyAdmin()) defaultSelected.push('agency_admin')
    if (this.$auth.stateAdmin()) defaultSelected.push('state_admin')
    this.userLevel = defaultSelected
    loadForm(
      'usersearch',
      'username',
      'lastName',
      'email',
      'selectedAgencies',
      'selectedStates',
      'agencyType',
      'status',
      'userLevel',
    ).bind(this)()
    this.checkRefreshAgencies()
  },
  beforeRouteEnter(_to, _from, next) {
    axios()
      .get('/api/misc/agencies', {
        params: { state: store.state.account.states.join(',') },
      })
      .then(resp => {
        next(vm => (vm.agencyList = resp.data))
      })
  },
  components: {
    FormHeader,
    CustomCheckbox,
    SuperSelect,
    CustomRadio,
  },
}
</script>

<style lang="scss" scoped>
.form-section {
  background: #f8f8f8;
  padding: 1.5rem;
}
</style>
